import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import LanguageIcon from '@mui/icons-material/Language';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { Box, Button, Chip, Grid, Stack, TextField, Tooltip, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { DataGrid } from '@mui/x-data-grid';

import CustomBackdrop from '../../../../components/CustomBackdrop/CustomBackdrop';
import {
  CompanyAssessmentType,
  CrawlerFindingTokenType,
  useDownvoteFindingsTokenMutation,
  useGetCompanySizeQuery,
  useGetCrawlerFindingTokensQuery,
  useSetCompanySizeMutation,
  useUpvoteFindingsTokenMutation,
} from '../../../../gql/generated/graphql';
import { getFirstNode, getNodes } from '../../../../gql/helpers';
import { handleVisitPage } from '../../../../helpers/helpers';
import { CompanySizeType } from '../../types';

type Props = {
  companyId: number;
  company: CompanyAssessmentType | null;
};

const ValidateSizeStep = ({ companyId, company }: Props) => {
  const { t } = useTranslation();
  const {
    data: crawlerFinding,
    loading: crawlerFindingLoading,
    refetch: refetchCrawlerFindingTokens,
  } = useGetCrawlerFindingTokensQuery({
    variables: { companyId },
  });
  const [setCompanySize, { loading: isSetCompanySizeLoading }] = useSetCompanySizeMutation();

  const { data: companySize, refetch: refetchGetCompanySize } = useGetCompanySizeQuery({
    variables: { companyId },
  });

  const crawlerFindingsNodes: CrawlerFindingTokenType[] = getNodes(crawlerFinding?.getCrawlerFindingTokens);
  const companySizeNode = getFirstNode(companySize?.getCompany);

  useEffect(() => {
    if (crawlerFindingsNodes?.[0]?.url) {
      const fullUrl = constructFullUrl(crawlerFindingsNodes[0].url);
      setUrl(fullUrl);
    }
  }, [crawlerFindingsNodes, company?.crawlUrl]);

  const [url, setUrl] = useState('');
  const [findingUrl, setFindingUrl] = useState('');
  const [fte, setFte] = useState('');
  const [fteType, setFteType] = useState<number>(null);

  const [upVoteMutation, { loading: upVoteLoading }] = useUpvoteFindingsTokenMutation();
  const [downVoteMutation, { loading: downVoteLoading }] = useDownvoteFindingsTokenMutation();

  // Helper function to check if URL contains domain
  const hasFullDomain = (url: string): boolean => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  // Helper function to construct full URL for iframe and external links
  const constructFullUrl = (url: string): string => {
    if (!url) return '';
    if (hasFullDomain(url)) {
      return url;
    }
    // If URL doesn't have domain, prepend the crawl_url from the company
    if (company?.crawlUrl) {
      const baseUrl = company.crawlUrl.endsWith('/') ? company.crawlUrl.slice(0, -1) : company.crawlUrl;
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${baseUrl}${path}`;
    }
    return url;
  };

  const columns = [
    { field: 'fte', headerName: t('validation.fte'), width: 70 },
    { field: 'url', headerName: t('validation.url'), width: 300 },
    {
      field: 'type',
      headerName: 'Type',
      width: 60,
      editable: false,
      sortable: false,
      renderCell: params => {
        const handleImageClick = () => {
          if (params.row.imageUrl) {
            // Open image in a new popup window with appropriate size
            const imageWindow = window.open(
              '',
              'imageViewer',
              'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,location=no,status=no'
            );

            if (imageWindow) {
              imageWindow.document.write(`
                <html>
                  <head>
                    <title>Image Finding - ${params.row.url}</title>
                    <style>
                      body { 
                        margin: 0; 
                        padding: 20px; 
                        background: #f5f5f5; 
                        font-family: Arial, sans-serif;
                        text-align: center;
                      }
                      .header {
                        margin-bottom: 20px;
                        padding: 10px;
                        background: white;
                        border-radius: 8px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                      }
                      .header h3 {
                        margin: 0 0 10px 0;
                        color: #333;
                      }
                      .header p {
                        margin: 5px 0;
                        color: #666;
                        font-size: 14px;
                      }
                      img { 
                        max-width: 100%; 
                        max-height: 70vh; 
                        border-radius: 8px;
                        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                        background: white;
                        padding: 10px;
                      }
                      .error {
                        color: #d32f2f;
                        background: #ffebee;
                        padding: 20px;
                        border-radius: 8px;
                        margin: 20px;
                      }
                    </style>
                  </head>
                  <body>
                    <div class="header">
                      <h3>Image Finding</h3>
                      <p><strong>Source Page:</strong> ${params.row.url}</p>
                      <p><strong>People Detected:</strong> ${params.row.fte}</p>
                      <p><strong>Image URL:</strong> ${params.row.imageUrl}</p>
                    </div>
                    <img src="${params.row.imageUrl}" alt="Image finding from ${params.row.url}" 
                         onerror="this.style.display='none'; document.querySelector('.error').style.display='block';" />
                    <div class="error" style="display: none;">
                      <h4>Unable to load image</h4>
                      <p>The image may be protected or no longer available.</p>
                      <p><a href="${params.row.imageUrl}" target="_blank">Try opening image URL directly</a></p>
                    </div>
                  </body>
                </html>
              `);
              imageWindow.document.close();
            }
          } else {
            console.warn('No image URL available for token:', params.row.id);
          }
        };

        return (
          <Box display="flex" gap={0.25} justifyContent="center" alignItems="center" height="100%">
            {params.row.isTextFinding && (
              <Tooltip title="This finding is based on text analysis (FTE count extracted from page text)" arrow>
                <Chip
                  icon={<TextFieldsIcon sx={{ fontSize: '16px' }} />}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: '20px',
                    minWidth: '20px',
                    '& .MuiChip-label': {
                      display: 'none',
                    },
                    '& .MuiChip-icon': {
                      marginLeft: 0,
                      marginRight: 0,
                    },
                    borderRadius: '50%',
                    padding: '2px',
                  }}
                />
              </Tooltip>
            )}
            {params.row.isImageFinding && (
              <Tooltip
                title="This finding is based on image analysis (people detected in images). Click to view image."
                arrow
              >
                <Chip
                  icon={<ImageIcon sx={{ fontSize: '16px' }} />}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  onClick={handleImageClick}
                  sx={{
                    height: '20px',
                    minWidth: '20px',
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      display: 'none',
                    },
                    '& .MuiChip-icon': {
                      marginLeft: 0,
                      marginRight: 0,
                    },
                    borderRadius: '50%',
                    padding: '2px',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s',
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 200,
      editable: false,
      sortable: false,
      renderCell: params => (
        <>
          <IconButton disabled={upVoteLoading} size="small" onClick={() => handleUpVote(params.row)}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton disabled={downVoteLoading} size="small" onClick={() => handleDownVote(params.row)}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleShow(params.row)}>
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleVisitPage(constructFullUrl(params.row.url))}>
            <LanguageIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const handleUpVote = async row => {
    const result = await upVoteMutation({
      variables: {
        tokenId: row.id,
      },
    });
    if (result?.data?.upvoteFindingsToken?.ok) {
      await refetchGetCompanySize();
    }
  };

  const handleDownVote = async row => {
    const result = await downVoteMutation({
      variables: {
        tokenId: row.id,
      },
    });
    if (result?.data?.downvoteFindingsToken?.ok) {
      await refetchGetCompanySize();
    }
  };

  const handleShow = row => {
    if (!row?.url) return;
    const fullUrl = constructFullUrl(row.url);
    setUrl(fullUrl);
  };

  const handleSetCompanySize = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!companyId || !fte) return;

    const result = await setCompanySize({
      variables: {
        companyId,
        fte: parseInt(fte),
        findingUrl,
        typeOfFinding: fteType,
      },
    });

    if (result?.data?.setCompanySize?.ok) {
      setFindingUrl('');
      setFte('');
      setFteType(null);
      await refetchCrawlerFindingTokens();
      await refetchGetCompanySize();
    }
  };

  const canSubmit = () => {
    if (fteType === CompanySizeType.IMAGE || fteType === CompanySizeType.TEXT) {
      return Boolean(fte && findingUrl);
    }
    if (fteType === CompanySizeType.LINKEDIN || fteType === CompanySizeType.OTHER) {
      return Boolean(fte);
    }
    return false;
  };

  return (
    <Box display="flex" flex={1} flexDirection="column">
      <Typography fontWeight="medium">
        {t('validation.currentSizeText')}
        {` ${companySizeNode?.maxFte || ''}`}
      </Typography>
      <Typography fontWeight="medium">
        {t('validation.linkedInFteText')}
        {` ${companySizeNode?.linkedinFte || ''}`}
      </Typography>

      {/* Size Justification */}
      {companySizeNode?.sizeJustification && (
        <Box mb={3} mt={2}>
          <Typography variant="body2" fontWeight="medium" color="primary" mb={1}>
            Size Justification
          </Typography>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#fafafa',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {companySizeNode.sizeJustification}
            </Typography>
          </Box>
        </Box>
      )}

      <Stack mt={2} direction={{ xs: 'column', md: 'row' }} gap={1}>
        <DataGrid
          disableRowSelectionOnClick
          loading={crawlerFindingLoading}
          rows={crawlerFindingsNodes}
          columns={columns}
          rowHeight={35}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[15]}
        />
        <Box flex={1} display="flex">
          <iframe
            width="100%"
            height="100%"
            title="fte"
            src={url}
            sandbox="allow-forms allow-popups allow-modals allow-downloads allow-scripts allow-same-origin"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      </Stack>
      <Box mt={2} component="form" onSubmit={handleSetCompanySize} noValidate autoComplete="off">
        <Typography>Set size manually</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label={fteType === CompanySizeType.LINKEDIN ? 'LinkedIn URL' : 'URL'}
              variant="outlined"
              name="url"
              value={findingUrl}
              onChange={e => setFindingUrl(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label="FTE"
              variant="outlined"
              name="fte"
              value={fte}
              type="number"
              onChange={e => setFte(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl>
              <FormLabel id="fte_type">Type</FormLabel>
              <RadioGroup
                value={fteType}
                onChange={e => setFteType(parseInt(e.target.value))}
                row
                aria-labelledby="fte-type"
                name="fte-type"
              >
                <FormControlLabel value={1} control={<Radio />} label="Image" />
                <FormControlLabel value={2} control={<Radio />} label="Text" />
                <FormControlLabel value={4} control={<Radio />} label="Linkedin" />
                <FormControlLabel value={3} control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              disabled={isSetCompanySizeLoading || !canSubmit()}
              variant="contained"
              color="primary"
              type="submit"
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Box>
      <CustomBackdrop open={isSetCompanySizeLoading || upVoteLoading || downVoteLoading} />
    </Box>
  );
};

export default ValidateSizeStep;
//
